import { constants } from "config";
import { getApiToken } from "utils/storage";

class ApiError extends Error {
  status: number;

  errorCode?: string;

  constructor(message: string, status: number, errorCode?: string) {
    super(message);
    this.errorCode = errorCode;
    this.status = status;
  }
}

const defaultOptions = {
  method: "GET",
  headers: {
    "Content-Type": "application/json",
  },
};

const api = async (path: string, options: RequestInit = {}) => {
  const token = getApiToken();

  const response = await window.fetch(`${constants.apiUrl}${path}`, {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      Authorization: token ? `Bearer ${token}` : "",
      ...options.headers,
    },
  });

  if (!response.ok) {
    let error: ApiError;
    try {
      // See if API returned known error message and code
      const { message, errorCode } = await response.json();
      error = new ApiError(message, response.status, errorCode);
    } catch {
      // API didn't return anything useful, so figure it out from HTTP status codes
      error = new ApiError("Server error", response.status);
    }

    // NOTE: React-Query will catch this thrown error for use in `useQuery()` status and error
    // value. If api client is used outside useQuery/useMutation, you need to handle thrown error
    // within your own `.catch()` block.
    throw error;
  }

  return response.json();
};

/**
 * Async API call to check approval list
 */
const getMintPermitted = (wallet: string) => api(`/mintApproval/${wallet}`);

type GetMintPermittedResponse = {
  isCurator: boolean;
  isAdmin: boolean;
  oneFree: boolean;
  approved: boolean;
};

/**
 * Async API call to get digital signature for mint
 */
const getMintApproval = (walletId: string) => {
  const options = {
    method: "POST",
    body: JSON.stringify({ walletId }),
  };
  return api("/mintApproval", options);
};
type GetMintApprovalResponse = {
  isCurator: boolean;
  oneFree: boolean;
  signature: string;
};

/**
 * Async API call to get meta data for set of token ids
 */
const postHugPassInfo = (tokenIds: string[]) => {
  const options = {
    method: "POST",
    body: JSON.stringify({ tokenIds }),
  };
  return api(`/hugpass`, options);
};
type HugPassInfoResponse = {
  name: string;
  side: string;
  description: string;
  imageUri: string;
  metaDataUri: string;
};

export type {
  GetMintPermittedResponse,
  GetMintApprovalResponse,
  HugPassInfoResponse,
};
export { api, ApiError, getMintPermitted, getMintApproval, postHugPassInfo };
